/**
 * Browser push-notification bootstrap.
 *
 * Orchestrates Service Worker registration and Web Push subscription. Every
 * entry point is defensive: push is a progressive enhancement on top of email
 * notifications, so any failure (unsupported browser, denied permission,
 * missing VAPID key, network error) is logged and swallowed — the app
 * continues to work email-only.
 *
 * The VAPID public key is read from the build-time env var
 * `VITE_VAPID_PUBLIC_KEY`, which MUST match the backend's `VAPID_PUBLIC_KEY`.
 * If it is absent, push subscription is skipped silently.
 */

import { apiClient } from "./apiClient";

/**
 * Convert a URL-safe base64 VAPID public key into the `Uint8Array` that
 * `PushManager.subscribe` expects as `applicationServerKey`.
 */
function urlBase64ToUint8Array(base64String: string): Uint8Array<ArrayBuffer> {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");

  const rawData = atob(base64);
  const buffer = new ArrayBuffer(rawData.length);
  const outputArray = new Uint8Array(buffer);
  for (let i = 0; i < rawData.length; i += 1) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

/**
 * Register the Service Worker at `/sw.js`.
 *
 * @returns the registration, or `null` if Service Workers are unsupported.
 */
export async function registerServiceWorker(): Promise<ServiceWorkerRegistration | null> {
  if (!("serviceWorker" in navigator)) {
    return null;
  }
  return navigator.serviceWorker.register("/sw.js");
}

/**
 * Subscribe the current browser to Web Push and persist the subscription on
 * the backend. Returns gracefully (without throwing) when push is unavailable
 * or the user declines permission.
 */
export async function subscribeToPush(
  registration: ServiceWorkerRegistration,
): Promise<void> {
  if (!("PushManager" in window) || typeof Notification === "undefined") {
    return;
  }

  // Ask for permission only when the user hasn't decided yet.
  let permission = Notification.permission;
  if (permission === "default") {
    permission = await Notification.requestPermission();
  }
  if (permission !== "granted") {
    // User denied (or dismissed) — app continues with email-only notifications.
    console.info("Push permission not granted; continuing email-only.");
    return;
  }

  const vapidPublicKey = import.meta.env.VITE_VAPID_PUBLIC_KEY;
  if (!vapidPublicKey) {
    console.warn(
      "VITE_VAPID_PUBLIC_KEY is not set; skipping push subscription.",
    );
    return;
  }

  // Reuse an existing subscription if present, otherwise create a new one.
  const subscription =
    (await registration.pushManager.getSubscription()) ??
    (await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(vapidPublicKey),
    }));

  const json = subscription.toJSON();
  const keys = json.keys ?? {};
  if (!json.endpoint || !keys.p256dh || !keys.auth) {
    console.warn("Incomplete push subscription; skipping backend registration.");
    return;
  }

  await apiClient.post("/api/push/subscribe", {
    endpoint: json.endpoint,
    p256dh: keys.p256dh,
    auth: keys.auth,
  });
}

/**
 * Entry point: register the Service Worker and subscribe to push. Wrapped so
 * that no failure ever propagates to the caller / crashes the app.
 */
export async function initPush(): Promise<void> {
  try {
    const registration = await registerServiceWorker();
    if (!registration) return;
    await subscribeToPush(registration);
  } catch (err) {
    console.error("Push initialization failed; continuing email-only:", err);
  }
}
